'use client'

import * as React from 'react'
import { X, Loader2, ChevronDown, ChevronUp, MapPin, HelpCircle, Search, Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore, useUIStore } from '@/lib/store'
import type { RecentSearch, TransactionType } from '@/lib/types'
import { useTypewriter } from '@/lib/useTypewriter'
import { api } from '@/lib/api'
import { LocationSelector } from './location-selector'
import { RecentSearchesDropdown } from './recent-searches-dropdown'
import { useDictation, DictationMicButton } from './dictation'
import { useI18n } from '@/lib/i18n'

interface SearchInputProps {
  onSearch: () => void
  className?: string
  selectedCity: string
  onCityChange: (city: string) => void
  selectedDistricts: string[]
  onDistrictsChange: (districts: string[]) => void
  /** Gate-only quick chips that append/remove their phrase in the query text
      (the minimal stand-in for FilterTags, which the gate hides). */
  showQueryChips?: boolean
}

// Quick query chips (gate): the old FilterTags' most-used shortcuts, phrased
// in the same NL grammar as EXAMPLE_QUERIES. Tap appends the phrase to the
// query (visible, single source of truth); tap again removes it. i18n'd so
// Czech users compose Czech queries.
export const QUERY_CHIPS = [
  { key: 'gate.chips.rooms2', fallback: '2+ rooms' },
  { key: 'gate.chips.under25k', fallback: 'under 25k' },
  { key: 'gate.chips.balcony', fallback: 'balcony' },
  { key: 'gate.chips.terrace', fallback: 'terrace' },
  { key: 'gate.chips.furnished', fallback: 'furnished' },
  { key: 'gate.chips.dishwasher', fallback: 'dishwasher' },
  { key: 'gate.chips.washer', fallback: 'washing machine' },
  { key: 'gate.chips.ac', fallback: 'air conditioning' },
  { key: 'gate.chips.elevator', fallback: 'elevator' },
  { key: 'gate.chips.bathtub', fallback: 'bathtub' },
  { key: 'gate.chips.parking', fallback: 'parking' },
  { key: 'gate.chips.pets', fallback: 'pet friendly' },
]

export const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const EXAMPLE_QUERIES = [
  "2 rooms, under 25k, Praha 3",
  "1+ room, under 10k total",
  "20 min from Anděl metro, furnished",
  "2 rooms, balcony",
  "3+ rooms, parking",
  "in Praha 7",
  "15 min from Strossmayerovo náměstí",
  "near Florenc, furnished",
  "in Praha 12, pet friendly",
  "2+1, balcony, under 20k",
  "dishwasher, washing machine",
  "with desk",
  "bathtub, under 25k",
  "bright, balcony",
]

export function SearchInput({
  onSearch,
  className,
  selectedCity,
  onCityChange,
  selectedDistricts,
  onDistrictsChange,
  showQueryChips = false,
}: SearchInputProps) {
  const { t } = useI18n()
  const { query, setQuery, isLoading, filters, updateFilter, setFilters, clearFilters, setSelectedCity: setStoreCity, setSelectedDistricts: setStoreDistricts, setTravelTime, addTag, clearTags, setSort, activeTags, filterCount, isCountLoading } = useSearchStore()
  // Dictation appends into the store query — same engine as the mobile sheet.
  const dictation = useDictation(() => query, setQuery)
  const { setTourOpen } = useUIStore()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const containerRef = React.useRef<HTMLFormElement>(null)
  const [showExamples, setShowExamples] = React.useState(false)
  const [cities, setCities] = React.useState<string[]>([])
  const [citiesLoading, setCitiesLoading] = React.useState(true)
  const [dropdownOpen, setDropdownOpen] = React.useState(false)
  const [citySearchQuery, setCitySearchQuery] = React.useState('')
  const citySearchInputRef = React.useRef<HTMLInputElement>(null)
  const [transactionTypeDropdownOpen, setTransactionTypeDropdownOpen] = React.useState(false)

  const filteredCities = React.useMemo(() => {
    if (!citySearchQuery.trim()) return cities
    const needle = citySearchQuery.trim().toLowerCase()
    return cities.filter((opt) => opt.toLowerCase().includes(needle))
  }, [cities, citySearchQuery])
  const [isFocused, setIsFocused] = React.useState(false)
  const [showRecentSearches, setShowRecentSearches] = React.useState(false)
  const [inputLeftPadding, setInputLeftPadding] = React.useState(180)
  const [isDesktop, setIsDesktop] = React.useState(false)

  // Count active filters (same logic as filter-tags)
  const numericFilterCount =
    (filters.rooms_min ? 1 : 0) + (filters.rooms_max ? 1 : 0) +
    (filters.area_min ? 1 : 0) + (filters.area_max ? 1 : 0) +
    (filters.price_min ? 1 : 0) + (filters.price_max ? 1 : 0)
  const totalActiveFilters = activeTags.length + numericFilterCount

  // Track screen size for responsive padding
  React.useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 640)
    checkDesktop()
    window.addEventListener('resize', checkDesktop)
    return () => window.removeEventListener('resize', checkDesktop)
  }, [])
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const transactionTypeDropdownRef = React.useRef<HTMLDivElement>(null)
  const transactionTypeButtonRef = React.useRef<HTMLButtonElement>(null)
  const recentSearchesRef = React.useRef<HTMLDivElement>(null)
  
  // Keep the input's left padding in sync with the transaction-type button's
  // ACTUAL width. A ResizeObserver catches every cause of a width change —
  // language switch (Rent → Pronájem), transaction type, font swap, and the
  // sm-breakpoint show/hide — whereas the old [filters.transaction_type]
  // dependency missed language changes, so the placeholder stayed offset by
  // the previous language's button width until a reload re-measured on mount.
  React.useEffect(() => {
    const btn = transactionTypeButtonRef.current
    if (!btn || typeof ResizeObserver === 'undefined') return
    // button width + 24px (12px gap on each side)
    const measure = () => setInputLeftPadding(btn.offsetWidth + 24)
    const ro = new ResizeObserver(measure)
    ro.observe(btn)
    measure()
    return () => ro.disconnect()
  }, [])

  // Typewriter animation for placeholder
  const animatedPlaceholder = useTypewriter(EXAMPLE_QUERIES, {
    typingSpeed: 80,
    deletingSpeed: 40,
    pauseDuration: 2500,
    paused: isFocused || query.length > 0,
  })
  
  // Fetch cities on mount
  React.useEffect(() => {
    const fetchCities = async () => {
      try {
        const data = await api.getCities()
        setCities(data)
      } catch (error) {
        console.error('Failed to fetch cities:', error)
        // Fallback to Praha if fetch fails
        setCities(['Praha'])
      } finally {
        setCitiesLoading(false)
      }
    }
    fetchCities()
  }, [])
  
  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
        setCitySearchQuery('')
      }
      if (transactionTypeDropdownRef.current && !transactionTypeDropdownRef.current.contains(event.target as Node)) {
        setTransactionTypeDropdownOpen(false)
      }
      if (recentSearchesRef.current && !recentSearchesRef.current.contains(event.target as Node)) {
        setShowRecentSearches(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle selecting a recent search — restore all state, then trigger search
  const handleSelectRecentSearch = React.useCallback((search: RecentSearch) => {
    setShowRecentSearches(false)

    if (search.type === 'text' && search.query) {
      // Text search: set query, clear filters
      setQuery(search.query)
      clearFilters()
    } else {
      // Filter search: restore filters, clear query
      setQuery('')
      if (search.filters) {
        setFilters({ ...search.filters })
      }
      clearTags()
      search.tags?.forEach((tag) => addTag(tag))
    }

    // Restore common state
    if (search.city) {
      onCityChange(search.city)
      setStoreCity(search.city)
    }
    if (search.districts) {
      onDistrictsChange(search.districts)
      setStoreDistricts(search.districts)
    }
    if (search.travelTime !== undefined) {
      setTravelTime(search.travelTime)
    }
    if (search.sort) {
      setSort(search.sort)
    }

    // Trigger the search after state is restored
    // Use setTimeout to ensure state updates have propagated
    setTimeout(() => onSearch(), 0)
  }, [onSearch, setQuery, clearFilters, setFilters, clearTags, addTag, onCityChange, onDistrictsChange, setStoreCity, setStoreDistricts, setTravelTime, setSort])
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setShowRecentSearches(false)
    onSearch()
  }
  
  const handleClear = () => {
    setQuery('')
    inputRef.current?.focus()
  }
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (showRecentSearches) {
        setShowRecentSearches(false)
      } else {
        handleClear()
      }
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
    setShowRecentSearches(true)
    
    // On mobile, scroll input into view when focused to ensure it's not hidden by keyboard
    if (!isDesktop && containerRef.current) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        })
      }, 100)
    }
  }
  
  // Query-chip helpers: active = phrase present in the query text (however it
  // got there — typed or tapped). Toggle keeps the input the single source of
  // truth; removal tidies the comma joins.
  const chipActive = (phrase: string) => query.toLowerCase().includes(phrase.toLowerCase())
  const toggleChip = (phrase: string) => {
    if (chipActive(phrase)) {
      const next = query
        .replace(new RegExp(`(,\\s*)?${escapeRegExp(phrase)}`, 'i'), '')
        .replace(/^\s*,\s*/, '')
        .replace(/\s{2,}/g, ' ')
        .trim()
      setQuery(next)
    } else {
      setQuery(query.trim() ? `${query.replace(/[,\s]+$/, '')}, ${phrase}` : phrase)
    }
    inputRef.current?.focus()
  }

  return (
    <form onSubmit={handleSubmit} className={cn('relative', className)} ref={containerRef}>
      {/* City selector and Location chips - inline, left-aligned with the
          input's leading edge (user-directed 2026-07-08) */}
      <div className="flex flex-wrap items-center justify-start gap-2 mb-5">
        {/* Transaction type selector - mobile only (shown in input on desktop) */}
        <div ref={transactionTypeDropdownRef} className="sm:hidden relative">
          <button
            type="button"
            onClick={() => setTransactionTypeDropdownOpen(!transactionTypeDropdownOpen)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full",
              // Solid frosted surface — these chips float over the hero
              // illustration; a 10% primary tint was invisible there.
              "bg-card/80 backdrop-blur-sm border border-border/70 shadow-sm text-primary text-sm font-medium",
              "hover:bg-card transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-primary/30"
            )}
          >
            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-muted-foreground font-normal">{t('search.transactionLabel', 'Transaction:')}</span>
            <span>{t(`search.${filters.transaction_type || 'rent'}`, filters.transaction_type || 'rent')}</span>
            <ChevronDown className={cn(
              "w-3.5 h-3.5 transition-transform",
              transactionTypeDropdownOpen && "rotate-180"
            )} />
          </button>

          {transactionTypeDropdownOpen && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 min-w-[140px] max-h-[300px] overflow-y-auto rounded-lg bg-popover border border-border shadow-lg">
              {(['rent', 'sale'] as TransactionType[]).map((transactionType) => (
                <button
                  key={transactionType}
                  type="button"
                  onClick={() => {
                    updateFilter('transaction_type', transactionType)
                    setTransactionTypeDropdownOpen(false)
                  }}
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm",
                    "hover:bg-accent transition-colors",
                    (filters.transaction_type || 'rent') === transactionType
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground"
                  )}
                >
                  {t(`search.${transactionType}`, transactionType)}
                </button>
              ))}
            </div>
          )}
        </div>

      {/* City selector dropdown */}
        <div ref={dropdownRef} className="relative" data-tour="city-selector">
          <button
            type="button"
            onClick={() => {
              const next = !dropdownOpen
              setDropdownOpen(next)
              if (next) setTimeout(() => citySearchInputRef.current?.focus(), 100)
              else setCitySearchQuery('')
            }}
            disabled={citiesLoading}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full",
              "bg-card/80 backdrop-blur-sm border border-border/70 shadow-sm text-primary text-sm font-medium",
              "hover:bg-card transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-primary/30"
            )}
          >
            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-muted-foreground font-normal">{t('search.cityLabel', 'City:')}</span>
            <span>{selectedCity || t('search.select', 'Select')}</span>
            <ChevronDown className={cn(
              "w-3.5 h-3.5 transition-transform",
              dropdownOpen && "rotate-180"
            )} />
          </button>
          
          {dropdownOpen && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 w-[220px] rounded-lg bg-popover border border-border shadow-lg overflow-hidden">
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    ref={citySearchInputRef}
                    type="text"
                    value={citySearchQuery}
                    onChange={(e) => setCitySearchQuery(e.target.value)}
                    placeholder={t('search.searchCities', 'Search cities...')}
                    className={cn(
                      "w-full pl-8 pr-3 py-1.5 text-sm rounded-md",
                      "bg-muted/50 border border-border",
                      "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    )}
                  />
                </div>
              </div>
              <div className="max-h-[240px] overflow-y-auto">
                {filteredCities.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                    {t('search.noCitiesFound', 'No cities found')}
                  </div>
                ) : (
                  filteredCities.map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => {
                        onCityChange(city)
                        setDropdownOpen(false)
                        setCitySearchQuery('')
                        onDistrictsChange([])
                      }}
                      className={cn(
                        "w-full px-3 py-2 text-left text-sm flex items-center justify-between",
                        "hover:bg-accent transition-colors",
                        selectedCity === city && "bg-primary/10 text-primary font-medium"
                      )}
                    >
                      <span>{city}</span>
                      {selectedCity === city && <Check className="w-4 h-4" />}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Location selector with chips */}
        <LocationSelector
          selectedCity={selectedCity}
          selectedDistricts={selectedDistricts}
          onDistrictsChange={onDistrictsChange}
        />
      </div>
      
      <div ref={recentSearchesRef} className="relative">
      <div className="flex flex-col sm:flex-row gap-3" data-tour="search-input">
        <div className="relative flex-1 h-16">
          {/* Transaction type dropdown - desktop only (shown in top row on mobile) */}
          <div ref={transactionTypeDropdownRef} className="hidden sm:block absolute left-3 top-1/2 -translate-y-1/2 z-10">
            <button
              ref={transactionTypeButtonRef}
              type="button"
              onClick={() => setTransactionTypeDropdownOpen(!transactionTypeDropdownOpen)}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full",
                "bg-primary/10 text-primary text-lg font-medium",
                "hover:bg-primary/20 transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary/30"
              )}
            >
              <span>{t(`search.${filters.transaction_type || 'rent'}`, filters.transaction_type || 'rent')}</span>
              <ChevronDown className={cn(
                "w-4 h-4 transition-transform",
                transactionTypeDropdownOpen && "rotate-180"
              )} />
            </button>

            {transactionTypeDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 min-w-[140px] max-h-[300px] overflow-y-auto rounded-lg bg-popover border border-border shadow-lg">
                {(['rent', 'sale'] as TransactionType[]).map((transactionType) => (
                  <button
                    key={transactionType}
                    type="button"
                    onClick={() => {
                      updateFilter('transaction_type', transactionType)
                      setTransactionTypeDropdownOpen(false)
                    }}
                    className={cn(
                      "w-full px-3 py-2 text-left text-base",
                      "hover:bg-accent transition-colors",
                      (filters.transaction_type || 'rent') === transactionType
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-foreground"
                    )}
                  >
                    {t(`search.${transactionType}`, transactionType)}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowRecentSearches(true) }}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={() => setIsFocused(false)}
            className={cn(
              'w-full h-16 rounded-xl',
              // Clears [× clear][mic] — mic only on voice-capable browsers.
              dictation.supported ? 'pr-20' : 'pr-12',
              'bg-card border-2 border-border',
              'text-foreground text-base sm:text-lg text-left placeholder:text-muted-foreground',
              'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
              'transition-all duration-200'
            )}
            style={{ paddingLeft: isDesktop ? `${inputLeftPadding}px` : '1rem' }}
            disabled={isLoading}
          />
          
          {/* Animated typewriter placeholder - only show when input is empty and not focused */}
          {!query && !isFocused && (
            <span
              className={cn(
                'absolute top-1/2 -translate-y-1/2 text-base sm:text-lg text-left text-muted-foreground pointer-events-none select-none whitespace-nowrap overflow-hidden',
                dictation.supported ? 'right-20' : 'right-12'
              )}
              style={{ left: isDesktop ? `${inputLeftPadding}px` : '1rem' }}
            >
                {animatedPlaceholder}
              <span className="inline-block w-[2px] h-5 ml-0.5 bg-muted-foreground/60 animate-pulse align-middle" />
            </span>
          )}
          
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {query && !isLoading && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 rounded-md hover:bg-muted transition-colors"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            {dictation.supported && !isLoading && (
              <DictationMicButton listening={dictation.listening} onToggle={dictation.toggle} className="p-1.5" />
            )}
            {isLoading && (
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
            )}
          </div>
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className={cn(
            'h-16 px-6 rounded-xl font-medium',
            'bg-primary text-white',
            'hover:bg-primary/90 active:bg-primary/80',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-all duration-200',
            'flex items-center justify-center gap-2',
            'w-full sm:w-auto'
          )}
        >
          {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
          <span>{t('search.searchButton', 'Search')}</span>
          {totalActiveFilters > 0 && !isLoading && (
            isCountLoading ? (
              <Loader2 className="h-4 w-4 animate-spin opacity-70" />
            ) : filterCount != null ? (
              <span className="px-2 py-0.5 text-xs bg-white/20 rounded-full">
                {filterCount.toLocaleString()}
              </span>
            ) : null
          )}
        </button>
      </div>

      {/* Recent searches dropdown */}
      <RecentSearchesDropdown
        visible={showRecentSearches}
        filterText={query}
        onSelect={handleSelectRecentSearch}
      />
      </div>

      {/* Quick query chips (gate) — ghost-weight, frosted like the City/
          District chips; active turns primary-tinted with a check. */}
      {showQueryChips && (
        <div className="mt-2.5 flex flex-wrap items-center justify-start gap-2">
          {QUERY_CHIPS.map(({ key, fallback }) => {
            const phrase = t(key, fallback)
            const active = chipActive(phrase)
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleChip(phrase)}
                aria-pressed={active}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-[11px] py-[5px] text-[13px] transition-colors',
                  active
                    ? 'border border-primary/35 bg-primary/10 font-medium text-primary'
                    : 'border border-border/70 bg-card/60 text-muted-foreground backdrop-blur-sm hover:bg-card hover:text-foreground'
                )}
              >
                {active ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                {phrase}
              </button>
            )
          })}
        </div>
      )}

      {/* relative anchor so the examples panel can float below the button
          row instead of growing this block — the gate hero is bottom-
          anchored, so in-flow growth pushed the whole stack upward. */}
      <div className="mt-3 relative">
        <div className="flex items-center justify-start gap-3">
          <button
            type="button"
            onClick={() => setShowExamples(!showExamples)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-tour="more-examples"
          >
            {showExamples ? (
              <>
                <ChevronUp className="h-5 w-5" />
                {t('search.hideExamples', 'Hide examples')}
              </>
            ) : (
              <>
                <ChevronDown className="h-5 w-5" />
                {t('search.moreExamples', 'More examples')}
              </>
            )}
          </button>
          
          <span className="text-muted-foreground/30 text-lg">•</span>
          
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <HelpCircle className="h-5 w-5" />
            {t('search.howItWorks', 'How it works')}
          </button>
        </div>
        
        {showExamples && (
          <div className="absolute left-0 top-full z-50 mt-2 w-[min(680px,92vw)] max-w-full rounded-2xl border border-border bg-popover p-3 shadow-lg flex flex-wrap justify-start gap-2">
            {EXAMPLE_QUERIES.map((example, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setQuery(example)
                  inputRef.current?.focus()
                }}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-full",
                  "bg-muted/50 text-muted-foreground",
                  "hover:bg-primary/10 hover:text-primary",
                  "transition-colors cursor-pointer"
                )}
              >
                {example}
              </button>
            ))}
          </div>
        )}
      </div>
    </form>
  )
}

